import { ThemeEvents } from '@theme/events';

/**
 * Campaign price display.
 *
 * Runs for every visitor. It asks the app proxy for the effective campaign price for this
 * visitor — the **public/campaign price** to everyone during its window, and the **VIP
 * price** additionally to a `vip_token` cookie holder — and rewrites matching product
 * prices so a campaign price renders like a native sale: the reduced price with the normal
 * price struck through, plus the "Spar X" savings badge. All markup comes from the Price
 * block (`snippets/price.liquid`) + the savings block (`blocks/price-savings.liquid`) —
 * this only fills values. Enforcement is the Function's job (checkout); this is display only.
 *
 * Anti-flash + freshness: the campaign is cached in sessionStorage and applied instantly
 * (so navigation doesn't flash), then reconciled against the proxy. Reconcile is
 * two-way — a product no longer in a live campaign (e.g. the window ended) is reverted to
 * its server-rendered normal price, so a stale cache can't leave an old campaign price on
 * a card. A network error (app down) is the one case we DON'T revert: we keep whatever is
 * showing, because the normal price is always server-rendered and never blank. Matching is
 * by product id (exact for single-variant products; variant-level is a follow-up).
 */

const COOKIE = 'vip_token';
const PROXY_URL = '/apps/vip/campaign';
const cacheKey = (token) => `vip-campaign:${token}`;

const readCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const gidToId = (gid) => String(gid || '').split('/').pop() || '';

/**
 * Format a money value (minor units) with the shop's `money_format` string, using the
 * standard Shopify placeholder replacement so it matches server-rendered prices.
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
const formatMoney = (cents, format) => {
  const amount = Number(cents);
  if (!Number.isFinite(amount)) return '';

  const withDelimiters = (precision, thousands, decimal) => {
    const fixed = (Math.abs(amount) / 100).toFixed(precision);
    const [whole, fractionRaw] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    // Mirror the theme's `money_without_trailing_zeros`: drop an all-zero decimal.
    const fraction = fractionRaw && /[1-9]/.test(fractionRaw) ? fractionRaw : '';
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };

  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, token) => {
    switch (token) {
      case 'amount':
        return withDelimiters(2, ',', '.');
      case 'amount_no_decimals':
        return withDelimiters(0, ',', '.');
      case 'amount_with_comma_separator':
        return withDelimiters(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return withDelimiters(0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return withDelimiters(2, "'", '.');
      case 'amount_with_space_separator':
        return withDelimiters(2, ' ', ',');
      case 'amount_no_decimals_with_space_separator':
        return withDelimiters(0, ' ', ',');
      default:
        return withDelimiters(2, ',', '.');
    }
  });
};

class CampaignPriceManager {
  #config = { moneyFormat: '{{amount}}' };

  /** @type {Map<string, { normalPrice: number, price: number, remaining?: number, launchLabel?: string }> | null} */
  #byProduct = null;

  /** @type {ReturnType<typeof setInterval> | null} */
  #polling = null;

  async init() {
    // Token is optional: a `vip_token` holder gets the VIP price, everyone gets the public
    // price during its window. The public read is keyed 'public' in the session cache.
    const token = readCookie(COOKIE);
    const cacheId = token ?? 'public';

    this.#readConfig();
    // Collection pages lazy-load / infinite-scroll more products, and filtering swaps
    // the grid — apply to any product-price elements added after the initial pass.
    this.#observeNewProducts();

    try {
      // Optimistic: apply from cache instantly (no flash on navigation).
      const cached = this.#readCache(cacheId);
      if (cached) {
        this.#ingest(cached);
        this.#applyAll();
      }

      // Authoritative: reconcile to the fresh proxy truth. This both applies matches and
      // reverts anything the cache applied that is no longer live (e.g. an ended window),
      // so a stale cache can't keep showing an old campaign price.
      const data = await this.#fetchCampaign(token);
      if (data?.valid) this.#writeCache(cacheId, data);
      else this.#clearCache(cacheId);
      this.#ingest(data);
      this.#applyAll();
    } catch (error) {
      // Network / app down: keep whatever the cache applied — the normal price is always
      // server-rendered, so nothing is ever blank, and we must NOT revert on a transient.
      console.error('[vip] campaign lookup failed', error);
    } finally {
      // Re-apply after the theme replaces the price DOM (variant change) or cart morph.
      document.addEventListener(ThemeEvents.variantUpdate, this.#onRerender);
      document.addEventListener(ThemeEvents.cartUpdate, this.#onRerender);
      // Keep the campaign fresh (scarcity ticking down, window ending) — but only while a
      // campaign is actually live for this visitor.
      this.#startPolling();
    }
  }

  #onVisible = () => {
    if (document.visibilityState === 'visible') this.#refresh();
  };

  /**
   * Poll the proxy to refresh the campaign as it runs — the "X left" scarcity count as units
   * sell, and reverting prices the moment the window ends. Gated on an actually-live campaign
   * (`#byProduct`): with no campaign there is nothing to refresh, so we stay silent instead of
   * hitting the proxy every 15s on every product page. The one-time load fetch still runs, so
   * a campaign already live when the page opens is picked up; one that *starts* while the page
   * sits open appears on the next navigation/reload, not mid-view. Only fires when the tab is
   * visible, and the proxy's short cache absorbs bursts — so it stays cheap. No time
   * countdown: the number just re-reads.
   */
  #startPolling() {
    if (this.#polling) return;
    if (!this.#byProduct) return;
    this.#polling = setInterval(() => {
      if (document.visibilityState === 'visible') this.#refresh();
    }, 15000);
    document.addEventListener('visibilitychange', this.#onVisible);
  }

  /** Stop polling and unbind its listener — used when a live campaign ends mid-view. */
  #stopPolling() {
    if (this.#polling) {
      clearInterval(this.#polling);
      this.#polling = null;
    }
    document.removeEventListener('visibilitychange', this.#onVisible);
  }

  async #refresh() {
    try {
      const token = readCookie(COOKIE);
      const cacheId = token ?? 'public';
      const data = await this.#fetchCampaign(token);
      if (data?.valid) this.#writeCache(cacheId, data);
      else this.#clearCache(cacheId);
      this.#ingest(data);
      this.#applyAll();
      // Campaign ended while the page was open — prices are now reverted; go quiet.
      if (!this.#byProduct) this.#stopPolling();
    } catch {
      /* keep the last good state — never blank */
    }
  }

  async #fetchCampaign(token) {
    const url = token ? `${PROXY_URL}?token=${encodeURIComponent(token)}` : PROXY_URL;
    const response = await fetch(url);
    return response.json();
  }

  #readCache(token) {
    try {
      const raw = sessionStorage.getItem(cacheKey(token));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  #writeCache(token, data) {
    try {
      sessionStorage.setItem(cacheKey(token), JSON.stringify(data));
    } catch {
      /* sessionStorage unavailable — non-fatal */
    }
  }

  #clearCache(token) {
    try {
      sessionStorage.removeItem(cacheKey(token));
    } catch {
      /* sessionStorage unavailable — non-fatal */
    }
  }

  /**
   * Set `#byProduct` from a proxy payload: a product map for a live, currency-matched
   * campaign, or `null` (no live campaign — callers then revert). Returns whether a map
   * was produced.
   * @param {any} data
   */
  #ingest(data) {
    this.#byProduct = this.#buildMap(data);
    return this.#byProduct != null;
  }

  /** @param {any} data */
  #buildMap(data) {
    if (!data?.valid) return null;
    const activeCurrency = window.Shopify?.currency?.active;

    const map = new Map();
    for (const product of data.products || []) {
      // Currency is per product now (campaigns can target different markets) — only show a
      // campaign whose currency matches the storefront, mirroring the Function's guard.
      if (product.currencyCode && activeCurrency && product.currencyCode !== activeCurrency) continue;
      const id = gidToId(product.productId);
      if (id) map.set(id, product);
    }
    return map.size ? map : null;
  }

  #onRerender = () => {
    requestAnimationFrame(() => this.#applyAll());
  };

  /**
   * Watch for product-price elements added after load (lazy-load / infinite scroll /
   * filtering on collection pages) and apply the campaign price to them too.
   */
  #observeNewProducts() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          const selector = 'product-price[data-product-id]';
          if (node instanceof Element && (node.matches(selector) || node.querySelector(selector))) {
            this.#onRerender();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  #readConfig() {
    const el = document.getElementById('campaign-config');
    if (!el?.textContent) return;
    try {
      this.#config = { ...this.#config, ...JSON.parse(el.textContent) };
    } catch {
      /* keep defaults */
    }
  }

  #money(major) {
    return formatMoney(Math.round(Number(major) * 100), this.#config.moneyFormat);
  }

  /** The effective campaign price for this visitor (proxy `price`; VIP or public). */
  #effective(product) {
    return Number(product.price);
  }

  /**
   * Reconcile every price + savings element on the page against the current campaign:
   * apply to products in a live campaign, revert the rest to their normal price. Runs on
   * load, on the proxy refresh, and after variant/cart re-renders.
   */
  #applyAll() {
    for (const priceEl of document.querySelectorAll('product-price[data-product-id]')) {
      const product = this.#byProduct?.get(gidToId(priceEl.getAttribute('data-product-id')));
      if (product) this.#applyOne(priceEl, product);
      else this.#revertOne(priceEl);
    }
    this.#applySavings();
    this.#applyScarcity();
  }

  /**
   * Fill the "KUN {n} TILBAGE" scarcity badge (n = proxy `remaining` = cap − sold) and
   * reveal it for a capped product in a live campaign; hide it otherwise (uncapped, sold
   * out, or no campaign). While shown it replaces the theme's sale/custom badge on the same
   * card — only one badge per image corner. PDP has no such badge, so nothing to hide there.
   */
  #applyScarcity() {
    for (const badge of document.querySelectorAll('[data-campaign-scarcity]')) {
      if (!(badge instanceof HTMLElement)) continue;
      const product = this.#byProduct?.get(badge.getAttribute('data-campaign-scarcity-product-id') || '');
      const remaining = typeof product?.remaining === 'number' ? product.remaining : null;
      const saleBadge = badge.closest('.card-gallery')?.querySelector('.product-badges');

      if (remaining != null && remaining > 0) {
        const countEl = badge.querySelector('[data-campaign-scarcity-count]');
        if (countEl && countEl.textContent !== String(remaining)) countEl.textContent = String(remaining);
        badge.hidden = false;
        if (saleBadge instanceof HTMLElement) saleBadge.style.display = 'none';
      } else {
        badge.hidden = true;
        if (saleBadge instanceof HTMLElement) saleBadge.style.removeProperty('display');
      }
    }
  }

  /**
   * @param {Element} productPrice
   * @param {{ normalPrice: number, price: number, launchLabel?: string }} product
   */
  #applyOne(productPrice, product) {
    const container = productPrice.querySelector('[ref="priceContainer"]');
    const priceEl = container?.querySelector('.price');
    if (!container || !(priceEl instanceof HTMLElement)) return;

    const campaign = this.#money(this.#effective(product));
    const normal = this.#money(product.normalPrice);
    if (!campaign || !normal) return;

    // Idempotent — the theme re-renders the same server HTML repeatedly.
    if (priceEl.dataset.campaignPrice === campaign) return;

    // Remember the server-rendered normal price so we can revert if the campaign ends.
    if (priceEl.dataset.campaignOriginal == null) {
      priceEl.dataset.campaignOriginal = priceEl.textContent ?? '';
    }

    priceEl.textContent = campaign;
    priceEl.dataset.campaignPrice = campaign;
    // Expose the effective campaign/VIP unit price in minor units (øre) so the add-on toggle
    // feature (component-product-toggle.js) builds its total on the campaign price, not the
    // server-rendered normal price.
    priceEl.dataset.campaignPriceCents = String(Math.round(this.#effective(product) * 100));
    priceEl.classList.add('campaign-price');

    // Launch price: the campaign price IS the intro price, so there is no genuine "before"
    // price — leave the compare slot hidden (no struck-through duplicate). The savings slot
    // shows the launch label instead (see #applySavings).
    if (!product.launchLabel) {
      // Struck-through normal price. All markup comes from price.liquid (the Price
      // block): a native sale already renders `.compare-at-price`; otherwise the block
      // renders a hidden `[data-campaign-compare]` slot we fill + reveal. We never
      // build price markup here.
      const compareEl = container.querySelector('.compare-at-price');
      if (compareEl instanceof HTMLElement) {
        if (compareEl.dataset.campaignOriginal == null) {
          compareEl.dataset.campaignOriginal = compareEl.textContent ?? '';
        }
        compareEl.textContent = normal;
      }
      const slot = container.querySelector('[data-campaign-compare]');
      if (slot instanceof HTMLElement) slot.hidden = false;
    }

    // Fade the price in so the normal → VIP swap reads as an intentional reveal.
    container.classList.add('campaign-applied');
  }

  /**
   * Undo a previously-applied campaign price, restoring the server-rendered normal price.
   * A no-op if this element was never touched, so it's safe to call on every product.
   * @param {Element} productPrice
   */
  #revertOne(productPrice) {
    const container = productPrice.querySelector('[ref="priceContainer"]');
    if (!container) return;

    const priceEl = container.querySelector('.price');
    if (priceEl instanceof HTMLElement && priceEl.dataset.campaignOriginal != null) {
      priceEl.textContent = priceEl.dataset.campaignOriginal;
      delete priceEl.dataset.campaignOriginal;
      delete priceEl.dataset.campaignPrice;
      delete priceEl.dataset.campaignPriceCents;
      priceEl.classList.remove('campaign-price');
    }

    const compareEl = container.querySelector('.compare-at-price');
    if (compareEl instanceof HTMLElement && compareEl.dataset.campaignOriginal != null) {
      compareEl.textContent = compareEl.dataset.campaignOriginal;
      delete compareEl.dataset.campaignOriginal;
    }

    // Re-hide the injected campaign compare slot (native sales have no such slot).
    const slot = container.querySelector('[data-campaign-compare]');
    if (slot instanceof HTMLElement) slot.hidden = true;

    container.classList.remove('campaign-applied');
  }

  /**
   * Reconcile the PDP/card "Spar X" savings badge (the third sale element).
   * price-savings.liquid renders a hidden slot keyed by product id; fill + reveal it for a
   * product in a live campaign, hide it otherwise. It stays hidden when there is no
   * campaign, so there are never empty badges.
   */
  #applySavings() {
    for (const badge of document.querySelectorAll('[data-campaign-savings]')) {
      if (!(badge instanceof HTMLElement)) continue;
      const product = this.#byProduct?.get(badge.getAttribute('data-campaign-savings-product-id') || '');
      if (!product) {
        this.#hideSavings(badge);
        continue;
      }

      // Launch price: show the merchant's label (e.g. "Lanceringspris") in place of the
      // computed "Spar X" — for an intro price there is no meaningful saving to show.
      let text;
      if (product.launchLabel) {
        text = String(product.launchLabel);
      } else {
        const savings = this.#money(Number(product.normalPrice) - this.#effective(product));
        if (!savings) {
          this.#hideSavings(badge);
          continue;
        }
        text = `${this.#config.labelSave || ''} ${savings}`.trim();
      }
      if (badge.dataset.campaignSavingsApplied === text) continue;

      badge.textContent = text;
      badge.dataset.campaignSavingsApplied = text;
      badge.style.display = '';
      badge.classList.add('campaign-applied');
    }
  }

  /** @param {HTMLElement} badge */
  #hideSavings(badge) {
    if (badge.dataset.campaignSavingsApplied == null) return; // never shown — leave as-is
    badge.textContent = '';
    delete badge.dataset.campaignSavingsApplied;
    badge.style.display = 'none';
    badge.classList.remove('campaign-applied');
  }
}

new CampaignPriceManager().init();
